import type { KafkaExtensionConfiguration } from '@/generated/http-clients/v3ApiSchemas'
import { applicationRootRoute } from '@/router/rootRoutes'
import { Box, Field, Switch } from '@chakra-ui/react'
import {
  ConfigFormManager,
  type FormModel,
  buildFormModel,
  convertJsonToXml,
  convertToXMLDocument,
  fetchXMLDocument,
  serializeDocument,
} from '@hivemq/xsd-forms'
import { createRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  type KafkaFlavor,
  baseXMLKafkaV3Flavor,
  calculateUiSchemaKafka,
  cleanupKafkaConfiguration,
} from '../utils/kafkaUtils'
import style from './KafkaExtensionPage.module.css'

export const kafkaExtensionRoute = createRoute({
  getParentRoute: () => applicationRootRoute,
  path: '/kafka-confluent',
  component: KafkaExtensionPage,
})

type Json = boolean | number | string | null | { [key: string]: Json } | Json[]

type KafkaExtensionPageProps = {
  flavor: KafkaFlavor
  initialConfig: KafkaExtensionConfiguration | undefined
  onSubmit: (json?: KafkaExtensionConfiguration) => void
}

export function KafkaExtensionPage({
  flavor,
  initialConfig,
  onSubmit,
}: Readonly<KafkaExtensionPageProps>) {
  const { t } = useTranslation('features', { useSuspense: false })
  const [expertModeActive, setExpertModeActive] = useState(false)

  const [formModel, setFormModel] = useState<FormModel | null>(null)

  const [jsonValues, setJsonValues] = useState<Json>({})

  const xsd = useMemo(() => {
    if (expertModeActive) {
      return fetchXMLDocument(
        'https://hivemq-cloud.github.io/xsd-serving/serve/4.25.0/xsd/hivemq-kafka-extension.xsd',
      )
    }
    return fetchXMLDocument(
      `${import.meta.env.BASE_URL}xsd/generic-kafka-starter/xsd/schema.xsd`,
    )
  }, [expertModeActive])

  function assignFormModel(xml: string) {
    buildFormModel(
      '',
      xsd,
      convertToXMLDocument(xml),
      calculateUiSchemaKafka(jsonValues, flavor),
    ).then((data) => {
      setFormModel(data)
    })
  }

  // reload the form model every time the base XML or the expert mode changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: we do not want to reload when assignFormModel changes
  useEffect(() => {
    // if an initial config is passed, transform it to XML and load it
    if (initialConfig) {
      const jsonInXml = convertJsonToXml(initialConfig)
      assignFormModel(jsonInXml)
      return
    }

    const baseXML = baseXMLKafkaV3Flavor(flavor)

    // otherwise load the empty Kafka XML
    assignFormModel(baseXML)
  }, [initialConfig, flavor])

  return (
    <>
      {formModel && (
        <Box
          p={4}
          backgroundColor="background.bg-base"
          border="1px solid"
          borderColor="border.border-base"
          borderRadius={6}
        >
          <Field.Root
            display="flex"
            alignItems="center"
            gap={2}
            w="auto"
            pb={4}
            disabled
          >
            <Switch.Root
              id="expert-mode"
              checked={expertModeActive}
              onCheckedChange={(details) =>
                setExpertModeActive(details.checked)
              }
            >
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
              <Switch.HiddenInput />
            </Switch.Root>
            <Field.Label htmlFor="expert-mode" mb="0" fontWeight="400">
              Expert mode
            </Field.Label>
          </Field.Root>
          <div className={style.kafkaConfiguration}>
            <ConfigFormManager
              key={`config_manager_${formModel.name}_${flavor}`}
              xsd={formModel.xsd.rootElement}
              title={formModel.title}
              keyRefs={formModel.xsd.keyRefs}
              uiSchema={calculateUiSchemaKafka(jsonValues, flavor)}
              xml={serializeDocument(formModel.xml)}
              onSubmit={(_xml, _files, json) => {
                const updatedConfig = cleanupKafkaConfiguration(json, flavor)
                onSubmit(updatedConfig)
              }}
              onChange={(_xml, json) => {
                setJsonValues(json)
              }}
              submitButtonText={t('extensions.saveConfiguration') ?? undefined}
            />
          </div>
        </Box>
      )}
    </>
  )
}
